import { type ReactElement } from "react";

import {
  createGetComponentFunc,
  getLighterColor,
} from "./design-element-utils";
import { PLATFORMS } from "./platforms";

interface GetColoredIconOptions {
  iconColor?: string;
  iconBackgroundColor?: string;
  borderColor?: string;
  size?: string;
  className?: string;
}

export type DesignElementBase = {
  iconColor: string;
  iconBackgroundColor?: string;
  getIcon: (options?: GetColoredIconOptions) => string;
  tags: string[];
};

export interface DesignElement extends DesignElementBase {
  getComponent: (props?: GetColoredIconOptions) => ReactElement;
  isPreset: boolean;
}

export type DesignElementKey = keyof typeof PLATFORMS;
export type DesignElementManagerType = {
  [key in DesignElementKey]: DesignElement;
} & DesignElementManager;

/*
  DO NOT USE THIS FUNCTION DIRECTLY:
  Use the useDesignElementManager hook instead
*/
export class DesignElementManager {
  #currentColorIndex = 0;
  readonly #COLORS = [
    "#7081FF", // cosmic
    "#71D5D5", // pluto
    "#C825A8", // venus
    "#F05F33", // mars
    "#FFD247", // sun
    "#50BA58", // alien
    "#F3971A", // jupiter
    "#681E06", // saturn
    "#004F2D", // earth
    "#CCA085", // phobos
  ];

  readonly #COLORS_V2 = [
    "#004F2D", // v2-priority-9
    "#71D5D5", // v2-priority-2
    "#FFD247", // v2-priority-5
    "#F05F33", // v2-priority-4
    "#C825A8", // v2-priority-3
    "#7081FF", // v2-priority-1
    "#50BA58", // v2-priority-6
    "#F3971A", // v2-priority-7
    "#681E06", // v2-priority-8
    "#CCA085", // v2-priority-10
    "#80A796", // v2-secondary-9
    "#88EAEA", // v2-secondary-2
    "#FFE8A3", // v2-secondary-5
    "#F7AF99", // v2-secondary-4
    "#E392D3", // v2-secondary-3
    "#A9B3FF", // v2-secondary-1
    "#ADDFB1", // v2-secondary-6
    "#F9CB8C", // v2-secondary-7
    "#B38E82", // v2-secondary-8
    "#E5CFC2", // v2-secondary-10
  ];

  readonly #colorMap: Map<string, string> = new Map();
  readonly #designElementMap: Map<string, DesignElementBase> = new Map();
  readonly #version: "v1" | "v2" = "v1";

  constructor(version: "v1" | "v2" = "v1") {
    this.#version = version;

    Object.entries(PLATFORMS).forEach(([key, designElementBase]) => {
      const designElement = createGetComponentFunc(designElementBase);
      PLATFORMS[key as DesignElementKey] = designElement;

      /* Create a map of all tags to their design element to quickly find
         the design element for a given platform */
      this.#designElementMap.set(key.toLowerCase(), designElement);
      designElement.tags.forEach((tag) => {
        this.#designElementMap.set(tag.toLowerCase(), designElement);
      });
    });

    Object.assign(this, PLATFORMS);
  }

  getDesignElement(ambiguousPlatform: string) {
    const sanitizedPlatform = ambiguousPlatform.toLowerCase().trim();
    const designElement = this.#designElementMap.get(sanitizedPlatform);
    if (!designElement) {
      return this.#getDesignElementForPreset(sanitizedPlatform);
    }

    return designElement as DesignElement;
  }

  getDesignElementColor(ambiguousPlatform: string) {
    const sanitizedPlatform = ambiguousPlatform.toLowerCase().trim();
    const designElement = this.#designElementMap.get(sanitizedPlatform);
    if (!designElement) {
      return this.#getColorForPreset(sanitizedPlatform);
    }
    return designElement.iconColor;
  }

  getColorForIndex(i: number): string {
    const colors = this.#version === "v1" ? this.#COLORS : this.#COLORS_V2;
    return colors[i % colors.length];
  }

  #getDesignElementForPreset(ambiguousPlatform: string) {
    const color = this.#getColorForPreset(ambiguousPlatform);
    return {
      ...createGetComponentFunc({
        ...PLATFORMS.preset,
        iconColor: color,
        iconBackgroundColor: getLighterColor(color),
      }),
      isPreset: true,
    };
  }

  #getColorForPreset(platform: string): string {
    if (this.#colorMap.has(platform)) {
      return this.#colorMap.get(platform)!;
    }
    const color = this.#COLORS[this.#getColorIndex()];
    this.#colorMap.set(platform, color);
    return color;
  }

  #getColorIndex() {
    if (this.#currentColorIndex === this.#COLORS.length) {
      this.#currentColorIndex = 0;
    }
    return this.#currentColorIndex++;
  }
}
